import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GetTimelinePostsResult } from 'common/interfaces/api';
import { useMediaQuery } from 'common/utils';
import { useMountedDelay } from 'common/utils/hooks/useDidMount';
import { convertOldPostToNewPost } from 'common/utils/new-post';
import { ScreenName } from 'common/utils/pp_tracking';
import InfinityScroll from 'components/v2/molecules/infinity-scroll';
import PostList from 'components/v2/organisms/post-list';
import { useInfiniteQuery } from 'react-query';
import { getProfileTimelinePosts } from 'service/user';
import { PostItemType } from 'service/timeline';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import ProfilePageContext from './profile-page-context';

interface ProfilePostsSectionPropsType {
  userId: number;
}

const ProfilePostsSection = ({ userId }: ProfilePostsSectionPropsType) => {
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const shouldRefetchPosts = useStore((state) => state.shouldRefetchPosts);
  const { setPinPosts } = useContext(ProfilePageContext);
  const mounted = useMountedDelay();
  const ableToShowSidebar = useMediaQuery('(max-width: 1199px)');
  const usingMounted = currentAuth ? true : mounted;
  const getNextPageParamProfile = useCallback(
    (lastPage: GetTimelinePostsResult) => {
      const posts = lastPage?.data?.posts;
      if (posts && posts.length > 0 && lastPage?.data?.next_key) {
        return lastPage?.data?.next_key;
      }
    },
    [],
  );

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    isFetched,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ['profile-posts', String(userId)],
    async ({ pageParam = '' }) => {
      if (!isAuthInit || !usingMounted) return Promise.reject();
      return getProfileTimelinePosts(
        userId,
        typeof pageParam === 'string' ? pageParam : null,
      );
    },
    {
      getNextPageParam: getNextPageParamProfile,
      enabled: isAuthInit && !!userId && usingMounted,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    },
  );

  const [isAuthDataReady, setIsAuthDataReady] = useState(false);

  const pages = data?.pages;
  const hasFetchedPosts = isFetched || !!pages;
  const posts: PostItemType[] = useMemo(
    () =>
      pages
        ? pages.reduce<PostItemType[]>((curr, page) => {
            const ps = page?.data?.posts || [];
            return curr.concat(
              ps.map((p) => {
                const post = convertOldPostToNewPost(p);
                if (!isAuthDataReady && post.user_info) {
                  return {
                    ...post,
                    user_info: { ...post.user_info, is_follow: true },
                  };
                }
                return post;
              }),
            );
          }, [])
        : [],
    [pages, isAuthDataReady],
  );

  // Extract pin posts from posts and save to context
  useEffect(() => {
    const pinPosts = posts.filter((post) => post.is_pinned);
    setPinPosts(pinPosts);
  }, [posts, setPinPosts]);

  useEffect(() => {
    if (shouldRefetchPosts) {
      void refetch();
    }
  }, [shouldRefetchPosts, refetch]);

  useEffect(() => {
    if (!isAuthInit) return;
    if (currentAuth?.uid) {
      void refetch().finally(() => setIsAuthDataReady(true));
    } else {
      setIsAuthDataReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAuth?.uid, isAuthInit]);

  return (
    <InfinityScroll
      fetchNextPage={fetchNextPage}
      isLoading={isFetchingNextPage}
      hasNextPage={hasNextPage}
    >
      <div className="">
        <PostList
          posts={posts}
          screen={ScreenName.Profile}
          isFetched={hasFetchedPosts}
          noPostMessage="まだ投稿がありません"
          hidePinPost={!ableToShowSidebar}
        />
      </div>
    </InfinityScroll>
  );
};

export default ProfilePostsSection;
